import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';

import {
  CURRENT_CONSENT_DOCUMENT_VERSION,
  resolveAllowedOnboardingStep,
  resolveOnboardingWizardStep,
  type OnboardingWizardStepKey,
} from '@solar/contracts';

import { AddressAutocompleteField } from '../../../components/addresses/AddressAutocompleteField';
import { Alert } from '../../../components/Alert';
import { Button } from '../../../components/Button';
import { FormField } from '../../../components/forms/FormField';
import { SelectField } from '../../../components/forms/SelectField';
import { PageSpinner } from '../../../components/PageSpinner';
import { customerDashboardApi } from '../../../features/customer-dashboard/customer-dashboard.api';
import { customersApi } from '../../../features/customers/customers.api';
import type { AddressAutocompleteSuggestion } from '../../../features/locations/locations.api';
import {
  acceptedConsentTypes,
  NIGERIAN_STATE_OPTIONS,
  ONBOARDING_STEP_DESCRIPTIONS,
  ONBOARDING_STEP_TITLES,
  parseOnboardingStepParam,
  REQUIRED_AGREEMENTS,
} from '../../../features/onboarding/onboarding-content';
import styles from '../../../features/onboarding/OnboardingWizard.module.css';
import { OnboardingWizardShell } from '../../../features/onboarding/OnboardingWizardShell';
import { useSessionQuery as useQuery } from '../../../hooks/useSessionQuery';
import { ApiError } from '../../../lib/api-error';
import { toDisplayMessage } from '../../../lib/error-message';
import { paths } from '../../../routes/paths';

function buildProgressInput(input: {
  hasCustomerProfile: boolean;
  hasNinDocument: boolean;
  addressCount: number;
  consents: readonly { consentType: string; documentVersion: string }[];
}) {
  return {
    hasCustomerProfile: input.hasCustomerProfile,
    hasNinDocument: input.hasNinDocument,
    addressCount: input.addressCount,
    recordedConsentTypes: [...acceptedConsentTypes(input.consents)],
  };
}

export function OnboardingPage(): React.JSX.Element {
  const queryClient = useQueryClient();
  const [searchParams, setSearchParams] = useSearchParams();

  const dashboard = useQuery({
    queryKey: ['customer', 'dashboard'],
    queryFn: () => customerDashboardApi.getDashboard(),
  });

  const profile = useQuery({
    queryKey: ['customer', 'profile'],
    queryFn: () => customersApi.getProfile(),
    retry: false,
  });

  const addresses = useQuery({
    queryKey: ['customer', 'addresses'],
    queryFn: () => customersApi.listAddresses(),
    enabled: profile.isSuccess,
  });

  const consents = useQuery({
    queryKey: ['customer', 'consents'],
    queryFn: () => customersApi.listConsents(),
    enabled: profile.isSuccess,
  });

  const documents = useQuery({
    queryKey: ['customer', 'documents'],
    queryFn: () => customersApi.listDocuments(),
    enabled: profile.isSuccess,
  });

  const hasProfile = profile.isSuccess;
  const missingProfile =
    profile.isError && profile.error instanceof ApiError && profile.error.status === 404;
  const addressCount =
    addresses.data?.filter((address) => address.addressType === 'RESIDENTIAL').length ?? 0;
  const consentRecords = useMemo(() => consents.data ?? [], [consents.data]);
  const ninDocuments = useMemo(() => documents.data ?? [], [documents.data]);
  const hasNinDocument = ninDocuments.some(
    (document) =>
      document.documentType === 'NIN_SLIP' &&
      (document.reviewStatus === 'PENDING_REVIEW' || document.reviewStatus === 'VERIFIED'),
  );

  const progressInput = useMemo(
    () =>
      buildProgressInput({
        hasCustomerProfile: hasProfile,
        hasNinDocument,
        addressCount,
        consents: consentRecords,
      }),
    [hasProfile, hasNinDocument, addressCount, consentRecords],
  );

  const requestedStep = parseOnboardingStepParam(searchParams.get('step'));
  const wizardStep = resolveOnboardingWizardStep(progressInput);
  const allowedStep = resolveAllowedOnboardingStep(requestedStep, progressInput);
  const activeStep: OnboardingWizardStepKey | 'complete' =
    wizardStep === 'complete' ? 'complete' : allowedStep;
  const progressIsLoading =
    dashboard.isPending ||
    profile.isPending ||
    (hasProfile && (addresses.isPending || consents.isPending || documents.isPending));

  const goToStep = useCallback(
    (step: OnboardingWizardStepKey | 'complete') => {
      if (step === 'complete') {
        setSearchParams({}, { replace: true });
        return;
      }

      setSearchParams({ step }, { replace: true });
    },
    [setSearchParams],
  );

  useEffect(() => {
    if (progressIsLoading) return;

    if (requestedStep === null && allowedStep !== 'complete' && allowedStep !== activeStep) {
      setSearchParams({ step: allowedStep }, { replace: true });
      return;
    }

    if (requestedStep !== null && allowedStep !== requestedStep && allowedStep !== 'complete') {
      setSearchParams({ step: allowedStep }, { replace: true });
    }
  }, [requestedStep, allowedStep, activeStep, progressIsLoading, setSearchParams]);

  const invalidateOnboarding = async (): Promise<void> => {
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: ['customer', 'dashboard'] }),
      queryClient.invalidateQueries({ queryKey: ['customer', 'profile'] }),
      queryClient.invalidateQueries({ queryKey: ['customer', 'addresses'] }),
      queryClient.invalidateQueries({ queryKey: ['customer', 'consents'] }),
      queryClient.invalidateQueries({ queryKey: ['customer', 'documents'] }),
    ]);
  };

  if (progressIsLoading) {
    return <PageSpinner label="Loading account setup" />;
  }

  if (activeStep === 'complete') {
    return (
      <div className={styles.wizard}>
        <div className={styles.inner}>
          <header className={styles.brandRow}>
            <span className={styles.brandWordmark}>SEAMONTECH</span>
          </header>

          <section className={styles.card} aria-labelledby="onboarding-complete-title">
            <div className={styles.completionIcon} aria-hidden="true">
              ✓
            </div>
            <h2 className={styles.stepTitle} id="onboarding-complete-title">
              You&apos;re all set
            </h2>
            <p className={styles.stepDescription}>
              Your Seamontech account setup is complete. You can now explore solar products and
              start an application when you are ready.
            </p>
            <div className={styles.completionActions}>
              <Link to={paths.customerProducts}>
                <Button>Explore solar products →</Button>
              </Link>
            </div>
          </section>
        </div>
      </div>
    );
  }

  if (activeStep === 'profile') {
    return (
      <ProfileStep
        profile={profile}
        missingProfile={missingProfile}
        onSaved={async () => {
          await invalidateOnboarding();
          goToStep('identity');
        }}
      />
    );
  }

  if (activeStep === 'identity') {
    return (
      <IdentityStep
        documents={ninDocuments}
        onSaved={async () => {
          await invalidateOnboarding();
          goToStep('address');
        }}
        onBack={() => {
          goToStep('profile');
        }}
      />
    );
  }

  if (activeStep === 'address') {
    return (
      <AddressStep
        addresses={addresses.data ?? []}
        onSaved={async () => {
          await invalidateOnboarding();
          goToStep('agreements');
        }}
        onBack={() => {
          goToStep('identity');
        }}
      />
    );
  }

  return (
    <AgreementsStep
      consents={consentRecords}
      onComplete={async () => {
        await invalidateOnboarding();
        goToStep('complete');
      }}
      onBack={() => {
        goToStep('address');
      }}
    />
  );
}

interface ProfileStepProps {
  readonly profile: ReturnType<
    typeof useQuery<Awaited<ReturnType<typeof customersApi.getProfile>>>
  >;
  readonly missingProfile: boolean;
  readonly onSaved: () => Promise<void>;
}

function ProfileStep({ profile, missingProfile, onSaved }: ProfileStepProps): React.JSX.Element {
  const [firstName, setFirstName] = useState('');
  const [middleName, setMiddleName] = useState('');
  const [lastName, setLastName] = useState('');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [occupation, setOccupation] = useState('');

  useEffect(() => {
    if (profile.data !== undefined) {
      setFirstName(profile.data.firstName);
      setMiddleName(profile.data.middleName ?? '');
      setLastName(profile.data.lastName);
      setPhoneNumber(profile.data.phoneNumber);
      setOccupation(profile.data.occupation ?? '');
    }
  }, [profile.data]);

  const save = useMutation({
    mutationFn: async () => {
      const payload = {
        firstName,
        middleName: middleName === '' ? undefined : middleName,
        lastName,
        phoneNumber,
        occupation: occupation === '' ? undefined : occupation,
      };

      if (missingProfile) {
        return customersApi.createProfile(payload);
      }

      return customersApi.updateProfile(payload);
    },
    onSuccess: async () => {
      await onSaved();
    },
  });

  return (
    <OnboardingWizardShell
      activeStep="profile"
      completedSteps={new Set()}
      title={ONBOARDING_STEP_TITLES.profile}
      description={ONBOARDING_STEP_DESCRIPTIONS.profile}
      backLabel={null}
      onBack={null}
      continueLabel="Save & continue"
      onContinue={() => {
        save.mutate();
      }}
      continueDisabled={
        firstName.trim() === '' || lastName.trim() === '' || phoneNumber.trim() === ''
      }
      continuePending={save.isPending}
    >
      {save.isError && <Alert tone="error">{toDisplayMessage(save.error)}</Alert>}
      {save.isSuccess && <Alert tone="success">Personal details saved.</Alert>}

      <div className={styles.formGrid}>
        <div className={styles.formRowTwo}>
          <FormField
            label="First name"
            name="firstName"
            value={firstName}
            required
            autoComplete="given-name"
            onChange={(event) => {
              setFirstName(event.target.value);
            }}
          />
          <FormField
            label="Last name"
            name="lastName"
            value={lastName}
            required
            autoComplete="family-name"
            onChange={(event) => {
              setLastName(event.target.value);
            }}
          />
        </div>

        <FormField
          label="Middle name"
          name="middleName"
          value={middleName}
          autoComplete="additional-name"
          onChange={(event) => {
            setMiddleName(event.target.value);
          }}
        />

        <FormField
          label="Phone number"
          name="phoneNumber"
          value={phoneNumber}
          required
          autoComplete="tel"
          onChange={(event) => {
            setPhoneNumber(event.target.value);
          }}
        />

        <FormField
          label="Occupation"
          name="occupation"
          value={occupation}
          onChange={(event) => {
            setOccupation(event.target.value);
          }}
        />
      </div>
    </OnboardingWizardShell>
  );
}

interface IdentityStepProps {
  readonly documents: Awaited<ReturnType<typeof customersApi.listDocuments>>;
  readonly onSaved: () => Promise<void>;
  readonly onBack: () => void;
}

function IdentityStep({ documents, onSaved, onBack }: IdentityStepProps): React.JSX.Element {
  const current = documents.find((document) => document.reviewStatus !== 'SUPERSEDED') ?? null;
  const [file, setFile] = useState<File | null>(null);
  const [fileError, setFileError] = useState<string | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [previewMime, setPreviewMime] = useState<string | null>(null);
  const previewUrlRef = useRef<string | null>(null);

  const replacePreviewUrl = (url: string | null): void => {
    if (previewUrlRef.current !== null) URL.revokeObjectURL(previewUrlRef.current);
    previewUrlRef.current = url;
    setPreviewUrl(url);
  };

  useEffect(() => {
    return () => {
      if (previewUrlRef.current !== null) URL.revokeObjectURL(previewUrlRef.current);
    };
  }, []);

  const upload = useMutation({
    mutationFn: async () => {
      if (file === null) throw new Error('Select your NIN slip before continuing.');
      return customersApi.uploadNin(file);
    },
    onSuccess: async () => {
      await onSaved();
    },
  });

  const openExistingPreview = async (): Promise<void> => {
    if (current === null) return;
    try {
      const blob = await customersApi.previewDocument(current.id);
      replacePreviewUrl(URL.createObjectURL(blob));
      setPreviewMime(current.mimeType);
      setFileError(null);
    } catch (error: unknown) {
      setFileError(toDisplayMessage(error));
    }
  };

  const canContinue =
    file !== null ||
    current?.reviewStatus === 'PENDING_REVIEW' ||
    current?.reviewStatus === 'VERIFIED';

  return (
    <OnboardingWizardShell
      activeStep="identity"
      completedSteps={new Set(['profile'])}
      title={ONBOARDING_STEP_TITLES.identity}
      description={ONBOARDING_STEP_DESCRIPTIONS.identity}
      backLabel="Personal details"
      onBack={onBack}
      continueLabel={file === null && canContinue ? 'Continue' : 'Upload & continue'}
      onContinue={() => {
        if (file === null && canContinue) void onSaved();
        else upload.mutate();
      }}
      continueDisabled={!canContinue || fileError !== null}
      continuePending={upload.isPending}
    >
      {upload.isError && <Alert tone="error">{toDisplayMessage(upload.error)}</Alert>}
      {current !== null && (
        <div className={styles.documentStatus}>
          <strong>NIN slip · {current.reviewStatus.replace(/_/g, ' ').toLowerCase()}</strong>
          <span>
            {current.originalFilename} · {(current.byteSize / 1_000_000).toFixed(2)} MB · uploaded{' '}
            {new Date(current.uploadedAt).toLocaleString()}
          </span>
          {current.reviewStatus === 'REJECTED' && (
            <Alert tone="error">
              Rejected{current.rejectionReason === null ? '' : ` — ${current.rejectionReason}`}.
              Upload a replacement to continue.
            </Alert>
          )}
          <Button type="button" variant="secondary" onClick={() => void openExistingPreview()}>
            Preview current document
          </Button>
        </div>
      )}

      <label className={styles.uploadField}>
        <span className={styles.selectLabel}>
          {current === null ? 'Upload NIN slip' : 'Upload replacement NIN slip'}
        </span>
        <span className={styles.uploadHint}>PDF, JPEG or PNG · maximum 5 MB</span>
        <input
          type="file"
          accept="application/pdf,image/jpeg,image/png"
          onChange={(event) => {
            const selected = event.target.files?.[0] ?? null;
            setFile(selected);
            replacePreviewUrl(selected === null ? null : URL.createObjectURL(selected));
            setPreviewMime(selected?.type ?? null);
            if (selected === null) setFileError(null);
            else if (!['application/pdf', 'image/jpeg', 'image/png'].includes(selected.type)) {
              setFileError('Choose a PDF, JPEG or PNG file.');
            } else if (selected.size > 5_000_000) {
              setFileError('The NIN slip must be 5 MB or smaller.');
            } else {
              setFileError(null);
            }
          }}
        />
      </label>

      {fileError !== null && <Alert tone="error">{fileError}</Alert>}
      {previewUrl !== null && (
        <div className={styles.documentPreview}>
          {previewMime === 'application/pdf' ? (
            <iframe
              src={previewUrl}
              title="NIN slip preview"
              sandbox=""
              referrerPolicy="no-referrer"
            />
          ) : (
            <img src={previewUrl} alt="NIN slip preview" />
          )}
        </div>
      )}
      <p className={styles.privacyNote}>
        Your document is stored privately and is available only to you and specifically authorized
        staff. Uploading lets onboarding continue while review remains pending.
      </p>
    </OnboardingWizardShell>
  );
}

interface AddressStepProps {
  readonly addresses: Awaited<ReturnType<typeof customersApi.listAddresses>>;
  readonly onSaved: () => Promise<void>;
  readonly onBack: () => void;
}

function AddressStep({ addresses, onSaved, onBack }: AddressStepProps): React.JSX.Element {
  const residentialAddresses = addresses.filter((address) => address.addressType === 'RESIDENTIAL');
  const defaultAddress =
    residentialAddresses.find((address) => address.isDefault === 'true') ??
    residentialAddresses[0] ??
    null;
  const [editing, setEditing] = useState(defaultAddress === null);

  const [addressType] = useState<string>('RESIDENTIAL');
  const [addressLine1, setAddressLine1] = useState(defaultAddress?.addressLine1 ?? '');
  const [addressLine2, setAddressLine2] = useState(defaultAddress?.addressLine2 ?? '');
  const [localGovernmentArea, setLocalGovernmentArea] = useState(
    defaultAddress?.localGovernmentArea ?? '',
  );
  const [city, setCity] = useState(defaultAddress?.city ?? '');
  const [stateCode, setStateCode] = useState(defaultAddress?.stateCode ?? '');
  const [postalCode, setPostalCode] = useState(defaultAddress?.postalCode ?? '');
  const [selectionNotice, setSelectionNotice] = useState(false);

  useEffect(() => {
    if (defaultAddress !== null && !editing) {
      setAddressLine1(defaultAddress.addressLine1);
      setAddressLine2(defaultAddress.addressLine2 ?? '');
      setLocalGovernmentArea(defaultAddress.localGovernmentArea ?? '');
      setCity(defaultAddress.city);
      setStateCode(defaultAddress.stateCode);
      setPostalCode(defaultAddress.postalCode ?? '');
    }
  }, [defaultAddress, editing]);

  const save = useMutation({
    mutationFn: async () => {
      if (defaultAddress !== null && editing) {
        return customersApi.updateAddress(defaultAddress.id, {
          addressLine1,
          addressLine2: addressLine2 === '' ? undefined : addressLine2,
          localGovernmentArea: localGovernmentArea === '' ? undefined : localGovernmentArea,
          city,
          stateCode,
          postalCode: postalCode === '' ? undefined : postalCode,
        });
      }

      return customersApi.addAddress({
        addressType,
        addressLine1,
        addressLine2: addressLine2 === '' ? undefined : addressLine2,
        localGovernmentArea: localGovernmentArea === '' ? undefined : localGovernmentArea,
        city,
        stateCode,
        countryCode: 'NG',
        postalCode: postalCode === '' ? undefined : postalCode,
        isDefault: 'true',
      });
    },
    onSuccess: async () => {
      await onSaved();
    },
  });

  const canContinue =
    defaultAddress !== null &&
    !editing &&
    addressLine1.trim() !== '' &&
    city.trim() !== '' &&
    stateCode.trim() !== '';

  return (
    <OnboardingWizardShell
      activeStep="address"
      completedSteps={new Set(['profile', 'identity'])}
      title={ONBOARDING_STEP_TITLES.address}
      description={ONBOARDING_STEP_DESCRIPTIONS.address}
      backLabel="Personal details"
      onBack={onBack}
      continueLabel="Save & continue"
      onContinue={() => {
        if (canContinue) {
          void onSaved();
          return;
        }

        save.mutate();
      }}
      continueDisabled={
        !canContinue &&
        (addressLine1.trim() === '' || city.trim() === '' || stateCode.trim() === '')
      }
      continuePending={save.isPending}
    >
      {save.isError && <Alert tone="error">{toDisplayMessage(save.error)}</Alert>}
      {save.isSuccess && <Alert tone="success">Address saved.</Alert>}
      {selectionNotice && (
        <Alert tone="info">Address details applied. Review them before you save.</Alert>
      )}

      {defaultAddress !== null && !editing ? (
        <>
          <article className={styles.savedAddress}>
            <h3 className={styles.savedAddressTitle}>
              {defaultAddress.addressType.replace(/_/g, ' ').toLowerCase()}
              {defaultAddress.isDefault === 'true' ? ' (primary)' : ''}
            </h3>
            <p className={styles.savedAddressBody}>
              {defaultAddress.addressLine1}
              {defaultAddress.addressLine2 !== null && defaultAddress.addressLine2 !== ''
                ? `, ${defaultAddress.addressLine2}`
                : ''}
              <br />
              {defaultAddress.city}, {defaultAddress.stateCode.replace(/_/g, ' ')}
            </p>
          </article>
          <Button
            type="button"
            variant="secondary"
            onClick={() => {
              setEditing(true);
            }}
          >
            Edit address
          </Button>
        </>
      ) : (
        <div className={styles.formGrid}>
          {defaultAddress === null && (
            <p className={styles.stepDescription}>
              This is your residential address. Your application will separately confirm where the
              solar system will be installed.
            </p>
          )}

          <AddressAutocompleteField
            value={addressLine1}
            required
            onChange={(value) => {
              setAddressLine1(value);
              setSelectionNotice(false);
            }}
            onSelect={(suggestion: AddressAutocompleteSuggestion) => {
              setAddressLine1(suggestion.line1 ?? suggestion.displayAddress);
              setAddressLine2(suggestion.line2 ?? '');
              setLocalGovernmentArea(suggestion.area ?? '');
              setCity(suggestion.city ?? '');
              setStateCode(suggestion.stateCode ?? '');
              setPostalCode(suggestion.postalCode ?? '');
              setSelectionNotice(true);
            }}
          />

          <FormField
            label="Address line 2"
            name="addressLine2"
            value={addressLine2}
            autoComplete="address-line2"
            onChange={(event) => {
              setAddressLine2(event.target.value);
            }}
          />

          <div className={styles.formRowTwo}>
            <FormField
              label="Local government area"
              name="localGovernmentArea"
              value={localGovernmentArea}
              onChange={(event) => {
                setLocalGovernmentArea(event.target.value);
              }}
            />
            <FormField
              label="Postal code"
              name="postalCode"
              value={postalCode}
              autoComplete="postal-code"
              onChange={(event) => {
                setPostalCode(event.target.value);
              }}
            />
          </div>

          <div className={styles.formRowTwo}>
            <FormField
              label="City"
              name="city"
              value={city}
              required
              autoComplete="address-level2"
              onChange={(event) => {
                setCity(event.target.value);
              }}
            />

            <SelectField
              label="State"
              value={stateCode}
              required
              onChange={(event) => {
                setStateCode(event.target.value);
              }}
            >
              <option value="" disabled>
                Choose state
              </option>
              {NIGERIAN_STATE_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </SelectField>
          </div>
        </div>
      )}
    </OnboardingWizardShell>
  );
}

interface AgreementsStepProps {
  readonly consents: Awaited<ReturnType<typeof customersApi.listConsents>>;
  readonly onComplete: () => Promise<void>;
  readonly onBack: () => void;
}

function AgreementsStep({ consents, onComplete, onBack }: AgreementsStepProps): React.JSX.Element {
  const accepted = acceptedConsentTypes(consents);
  const [checked, setChecked] = useState<Record<string, boolean>>({});
  const [submitError, setSubmitError] = useState<string | null>(null);

  const submit = useMutation({
    mutationFn: async () => {
      setSubmitError(null);
      const pending = REQUIRED_AGREEMENTS.filter(
        (agreement) => !accepted.has(agreement.type) && checked[agreement.type] === true,
      );

      for (const agreement of pending) {
        await customersApi.recordConsent({ consentType: agreement.type });
      }
    },
    onSuccess: async () => {
      await onComplete();
    },
    onError: (error) => {
      setSubmitError(toDisplayMessage(error));
    },
  });

  const remaining = REQUIRED_AGREEMENTS.filter((agreement) => !accepted.has(agreement.type));
  const allChecked =
    remaining.length === 0 || remaining.every((agreement) => checked[agreement.type] === true);

  return (
    <OnboardingWizardShell
      activeStep="agreements"
      completedSteps={new Set(['profile', 'identity', 'address'])}
      title={ONBOARDING_STEP_TITLES.agreements}
      description={ONBOARDING_STEP_DESCRIPTIONS.agreements}
      backLabel="Address"
      onBack={onBack}
      continueLabel="Accept agreements & continue"
      onContinue={() => {
        if (remaining.length === 0) {
          void onComplete();
          return;
        }

        submit.mutate();
      }}
      continueDisabled={!allChecked}
      continuePending={submit.isPending}
    >
      {submitError !== null && <Alert tone="error">{submitError}</Alert>}
      {submit.isSuccess && <Alert tone="success">Agreements recorded.</Alert>}

      <ul className={styles.agreementList}>
        {REQUIRED_AGREEMENTS.map((agreement) => {
          const isAccepted = accepted.has(agreement.type);

          return (
            <li
              key={agreement.type}
              className={`${styles.agreementItem} ${isAccepted ? styles.agreementItemAccepted : ''}`}
            >
              <label className={styles.agreementLabel}>
                <input
                  type="checkbox"
                  className={styles.agreementCheckbox}
                  checked={isAccepted || checked[agreement.type] === true}
                  disabled={isAccepted || submit.isPending}
                  onChange={(event) => {
                    setChecked((current) => ({
                      ...current,
                      [agreement.type]: event.target.checked,
                    }));
                  }}
                />
                <span className={styles.agreementCopy}>
                  <span className={styles.agreementTitle}>{agreement.title}</span>
                  <span className={styles.agreementDescription}>{agreement.description}</span>
                  {isAccepted && (
                    <span className={styles.agreementMeta}>
                      Accepted · version {CURRENT_CONSENT_DOCUMENT_VERSION}
                    </span>
                  )}
                </span>
              </label>
            </li>
          );
        })}
      </ul>
    </OnboardingWizardShell>
  );
}
